import { useEffect, useMemo, useRef, useState } from "react";
import { api, currency, fmtDate } from "@/lib/api";
import { downloadInvoicePdf, generatePdfBase64, makeDocFilename } from "@/lib/pdf";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import InvoiceDocument from "@/components/InvoiceDocument";
import EmailPreviewModal from "@/components/EmailPreviewModal";
import { Eye, Download, CheckCircle2, Clock, Search, Mail, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "outstanding", label: "Outstanding" },
  { key: "paid", label: "Paid" },
  { key: "all", label: "All" },
];

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [filter, setFilter] = useState("outstanding");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const docRef = useRef(null);

  // Send dialog
  const [sendInv, setSendInv] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    const { data } = await api.get("/invoices");
    setInvoices(data);
  };
  useEffect(() => { load(); }, []);

  const isOverdue = (inv) => inv && !inv.paid && inv.due_date && new Date(inv.due_date) < new Date();

  const filtered = useMemo(() => {
    let list = invoices;
    if (filter === "outstanding") list = invoices.filter(i => !i.paid);
    else if (filter === "paid") list = invoices.filter(i => i.paid);
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (inv) =>
        (inv.invoice_number || "").toLowerCase().includes(q) ||
        (inv.customer_name || "").toLowerCase().includes(q)
    );
  }, [invoices, filter, query]);

  const markPaid = async (id, paid, e) => {
    if (e) e.stopPropagation();
    try {
      const { data } = await api.patch(`/invoices/${id}/paid`, { paid });
      toast.success(paid ? "Marked as paid" : "Marked as outstanding");
      setSelected((s) => (s && s.id === id ? data : s));
      load();
    } catch { toast.error("Failed to update invoice"); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const filename = makeDocFilename(selected?.customer_tag, "Invoice", selected?.invoice_number);
      await downloadInvoicePdf(docRef.current, filename);
      toast.success("PDF downloaded");
    } catch { toast.error("Could not generate PDF"); }
    finally { setDownloading(false); }
  };

  const openSend = (inv, e) => {
    if (e) e.stopPropagation();
    setSendInv(inv);
    setSendEmail(inv.customer_email || "");
    setSendOpen(true);
  };

  const handleSend = async ({ toEmail, subject, customBodyHtml }) => {
    setSending(true);
    try {
      const filename = makeDocFilename(sendInv?.customer_tag, "Invoice", sendInv?.invoice_number);
      setSelected(sendInv);
      await new Promise((r) => setTimeout(r, 120));
      let pdf_base64 = null;
      if (docRef.current) pdf_base64 = await generatePdfBase64(docRef.current);
      await api.post(`/invoices/${sendInv.id}/send`, {
        to_email: toEmail,
        pdf_base64,
        filename,
        doc_type: "invoice",
        custom_subject: subject,
        custom_body_html: customBodyHtml,
      });
      toast.success(`Invoice sent to ${toEmail}`);
      setSendOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send email");
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="invoices-page">
      <h1 className="font-heading text-3xl font-bold text-[#032352]">Invoices</h1>
      <p className="text-[#5B5C5F] mt-1 mb-5">Invoice documents — view, download, email, and mark as paid.</p>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="invoices-filter-tabs">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`inv-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
              filter === f.key
                ? "bg-[#032352] text-white border-[#032352]"
                : "bg-white text-[#5B5C5F] border-slate-200 hover:border-[#032352] hover:text-[#032352]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="relative w-full max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5B5C5F]" />
        <Input
          data-testid="invoices-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by invoice # or customer..."
          className="pl-9"
        />
      </div>

      <div className="bg-white rounded-md border border-slate-200 overflow-x-auto">
        <Table className="min-w-[680px]">
          <TableHeader>
            <TableRow className="bg-[#F8FAFC]">
              <TableHead>Invoice #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Due</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="w-36"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-[#5B5C5F] py-10">No invoices found.</TableCell>
              </TableRow>
            )}
            {filtered.map((inv) => (
              <TableRow
                key={inv.id}
                data-testid={`invoice-row-${inv.id}`}
                className={`cursor-pointer ${isOverdue(inv) ? "bg-red-50 hover:bg-red-100" : ""}`}
                onClick={() => setSelected(inv)}
              >
                <TableCell className="font-mono-num font-medium text-[#032352]">{inv.invoice_number}</TableCell>
                <TableCell className="text-[#032352]">{inv.customer_name}</TableCell>
                <TableCell className="text-[#5B5C5F] text-sm">{fmtDate(inv.invoice_date)}</TableCell>
                <TableCell className={`text-sm font-medium ${isOverdue(inv) ? "text-red-600" : "text-[#5B5C5F]"}`}>
                  {fmtDate(inv.due_date)}
                  {isOverdue(inv) && <AlertCircle className="w-3 h-3 inline ml-1 text-red-500" />}
                </TableCell>
                <TableCell>
                  {inv.paid ? (
                    <Badge className="bg-emerald-500 hover:bg-emerald-500" data-testid={`status-${inv.id}`}>Paid</Badge>
                  ) : isOverdue(inv) ? (
                    <Badge className="bg-red-500 hover:bg-red-500 text-white" data-testid={`status-${inv.id}`}>Overdue</Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-600 border-amber-400" data-testid={`status-${inv.id}`}>Outstanding</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono-num text-[#032352]">{currency(inv.total)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-3 justify-end">
                    {!inv.paid ? (
                      <button data-testid={`mark-paid-${inv.id}`} onClick={(e) => markPaid(inv.id, true, e)} title="Mark as paid" className="text-slate-400 hover:text-emerald-600 transition-colors">
                        <CheckCircle2 className="w-4 h-4" />
                      </button>
                    ) : (
                      <button data-testid={`mark-unpaid-${inv.id}`} onClick={(e) => markPaid(inv.id, false, e)} title="Mark as outstanding" className="text-emerald-600 hover:text-amber-600 transition-colors">
                        <Clock className="w-4 h-4" />
                      </button>
                    )}
                    <button data-testid={`send-invoice-${inv.id}`} onClick={(e) => openSend(inv, e)} title="Email invoice" className="text-slate-400 hover:text-[#032352] transition-colors">
                      <Mail className="w-4 h-4" />
                    </button>
                    <button data-testid={`view-invoice-${inv.id}`} onClick={(e) => { e.stopPropagation(); setSelected(inv); }} title="View invoice" className="text-slate-400 hover:text-[#032352] transition-colors">
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Invoice view dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="invoice-view-dialog">
          <DialogTitle className="sr-only">Invoice {selected?.invoice_number}</DialogTitle>
          <DialogDescription className="sr-only">Invoice document for {selected?.customer_name}</DialogDescription>
          <div className="flex justify-between items-center gap-2 p-4 pb-0 flex-wrap">
            <div>
              {selected?.paid ? (
                <Badge className="bg-emerald-500 hover:bg-emerald-500">Paid</Badge>
              ) : isOverdue(selected) ? (
                <Badge className="bg-red-500 hover:bg-red-500 text-white">Overdue</Badge>
              ) : (
                <Badge variant="outline" className="text-amber-600 border-amber-400">Outstanding</Badge>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {selected && !selected.paid && (
                <Button size="sm" onClick={() => markPaid(selected.id, true)} data-testid="dialog-mark-paid-button" className="bg-emerald-500 hover:bg-emerald-600">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Paid
                </Button>
              )}
              {selected && selected.paid && (
                <Button size="sm" variant="outline" onClick={() => markPaid(selected.id, false)} data-testid="dialog-mark-unpaid-button" className="border-amber-400 text-amber-600">
                  <Clock className="w-4 h-4 mr-2" /> Mark Outstanding
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => openSend(selected)} data-testid="dialog-send-button" className="border-[#032352] text-[#032352]">
                <Mail className="w-4 h-4 mr-2" /> Email Invoice
              </Button>
              <Button variant="outline" size="sm" onClick={downloadPdf} disabled={downloading} data-testid="print-invoice-button" className="border-[#032352] text-[#032352]">
                <Download className="w-4 h-4 mr-2" /> {downloading ? "Generating…" : "Download PDF"}
              </Button>
            </div>
          </div>
          <div ref={docRef}>
            <InvoiceDocument invoice={selected} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Send invoice modal — with live preview + editing */}
      <EmailPreviewModal
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        title={`Send Invoice — ${sendInv?.invoice_number}`}
        toEmail={sendEmail}
        customerEmail={sendInv?.customer_email}
        docType="invoice"
        invoice={sendInv}
        attachmentName={sendInv ? makeDocFilename(sendInv.customer_tag, "Invoice", sendInv.invoice_number) : undefined}
        onSend={handleSend}
        sending={sending}
      />
    </div>
  );
}
